import React from 'react';
import { useFetcher, Form } from 'react-router-dom';

import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { AutoResizeTextarea, Textarea } from '@oysterjs/ui/textarea';
import { ActionResult } from '@oysterjs/core/graphql/actions';

import { FileInput } from '~/components/file';
import { useDefaultVisibilityValue } from '~/components/visibility/default-visibility';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import {
  visibilityInputFromValue,
  type VisibilitySelectorValue
} from '~/components/visibility/visibility-selector.utils';

import { ValidationError } from '@oysterjs/core/graphql';
import { useFormActions } from '@oysterjs/core/graphql/form';

import type { ExcelTemplateFormState } from '~/components/library/lib/excel-template-tabs';

const EXCEL_DEFAULT_DESCRIPTION =
  'Excel spreadsheet template for Cara to fill from your agency workbook.';

const emptyForm = (visibility: VisibilitySelectorValue): ExcelTemplateFormState => ({
  title: '',
  description: '',
  fillGuidance: '',
  tabs: [],
  visibility: visibilityInputFromValue(visibility)
});

export const EXCEL_EXTENSION_RE = /\.xlsx$/i;

export const EXCEL_EXTENSION_ERROR =
  'Upload a .xlsx file only, not .xls, .xlsm, .csv, or other formats.';

export interface ExcelTemplateCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** POST action URL, e.g. `/app/library/excel-templates/new`. The action is
   *  expected to perform the upload and revalidate the parent route. */
  action: string;
}

/**
 * Upload a .xlsx workbook to the library. Submits through the router fetcher so
 * the parent loader revalidates after upload, and closes itself once the action
 * returns idle without a payload error.
 *
 * The workbook's worksheets become the template's tabs, but they are only known
 * after ingestion reads the file — so this dialog collects workbook-level
 * metadata only, and per-tab guidance is authored afterwards in the editor.
 */
export function ExcelTemplateCreateDialog({
  open,
  onClose,
  action
}: ExcelTemplateCreateDialogProps) {
  const fetcher = useFetcher();

  const visibilityEnabled = useVisibilityEntitlement();
  const defaultVisibility = useDefaultVisibilityValue();
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(defaultVisibility);

  const [form, setForm] = React.useState<ExcelTemplateFormState>(() =>
    emptyForm(defaultVisibility)
  );
  const [uploadTemplate, setUploadTemplate] = React.useState<File | null>(null);
  /** Remount FileInput after reset so its internal attachment list clears. */
  const [fileInputKey, setFileInputKey] = React.useState(0);
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const { updateForm, getValidationError } = useFormActions<ExcelTemplateFormState>(
    setForm,
    setValidationErrors
  );
  const [error, setError] = React.useState<string>();

  /** True between handleSubmit and the next idle that follows it, so we only
   *  auto-close after our own submission rather than any parent revalidation. */
  const pendingSubmitRef = React.useRef(false);

  // The action runs during 'submitting'; revalidation uses 'loading'. Both
  // count as in-flight for the purposes of locking the UI.
  const fetcherInFlight = fetcher.state === 'submitting' || fetcher.state === 'loading';

  const routeActionPayload =
    fetcher.data && typeof fetcher.data === 'object'
      ? (fetcher.data as NonNullable<ActionResult<unknown>>)
      : null;
  const routeActionFailed =
    !!routeActionPayload &&
    (Boolean(routeActionPayload.applicationError) ||
      (routeActionPayload.validationErrors?.length ?? 0) > 0);

  const resetFields = React.useCallback(() => {
    setForm(emptyForm(defaultVisibility));
    setUploadTemplate(null);
    setFileInputKey((k) => k + 1);
    setVisibility(defaultVisibility);
    setValidationErrors([]);
    setError(undefined);
  }, [defaultVisibility]);

  React.useEffect(() => {
    pendingSubmitRef.current = false;
    if (open) {
      setError(undefined);
    }
  }, [open]);

  React.useEffect(() => {
    if (fetcher.state !== 'idle' || !pendingSubmitRef.current) return;
    pendingSubmitRef.current = false;
    if (routeActionFailed) {
      if (routeActionPayload?.applicationError) {
        setError(routeActionPayload.applicationError.message ?? 'Upload failed');
        return;
      }
      if (routeActionPayload?.validationErrors?.length) {
        setValidationErrors(routeActionPayload.validationErrors);
        return;
      }
    }
    // Clear fields before closing so the next "Upload Excel template" opens blank.
    resetFields();
    onClose();
  }, [fetcher.state, onClose, resetFields, routeActionFailed, routeActionPayload]);

  const handleClose = () => {
    if (fetcherInFlight) return;
    resetFields();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Reject a non-.xlsx file up front; every other rule (title, description,
    // missing file) is enforced server-side and surfaces through
    // validationErrors so each message lands on its own field.
    if (uploadTemplate && !EXCEL_EXTENSION_RE.test(uploadTemplate.name)) {
      setError(EXCEL_EXTENSION_ERROR);
      return;
    }
    setError(undefined);

    const formData = new FormData();
    formData.set('title', form.title);
    formData.set('description', form.description);
    formData.set('fillGuidance', form.fillGuidance ?? '');
    if (uploadTemplate) {
      formData.set('uploadTemplate', uploadTemplate);
    }
    if (visibilityEnabled) {
      formData.set('visibility', JSON.stringify(visibilityInputFromValue(visibility)));
    }

    pendingSubmitRef.current = true;
    fetcher.submit(formData, {
      method: 'POST',
      action,
      encType: 'multipart/form-data'
    });
  };

  const fileError = getValidationError(validationErrors, ['uploadTemplate']);

  return (
    <Dialog open={open} onClose={handleClose} size="2xl">
      <DialogTitle>Upload Excel template</DialogTitle>
      <DialogDescription>
        Add a .xlsx workbook to your library so Cara can fill it and produce new spreadsheets. Each
        worksheet becomes a tab you can give its own instructions once the workbook is processed.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Template title</Label>
                <Description>Shown in your Library list and in workspace tools.</Description>
                <Input
                  invalid={!!getValidationError(validationErrors, ['title'], { exact: true })}
                  value={form.title}
                  onChange={(e) => {
                    const title = e.currentTarget.value;
                    updateForm(['title'], (prev) => ({ ...prev, title }));
                  }}
                  placeholder="e.g. Carrier quote comparison"
                />
                {getValidationError(validationErrors, ['title'], { exact: true }) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['title'], { exact: true })}
                  </ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Description</Label>
                <Description>Helps your team recognize this template in the Library.</Description>
                <Textarea
                  rows={3}
                  invalid={!!getValidationError(validationErrors, ['description'])}
                  value={form.description}
                  onChange={(e) => {
                    const description = e.currentTarget.value;
                    updateForm(['description'], (prev) => ({ ...prev, description }));
                  }}
                  placeholder={EXCEL_DEFAULT_DESCRIPTION}
                />
                {getValidationError(validationErrors, ['description']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['description'])}
                  </ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Setup guidance (optional)</Label>
                <Description>
                  Extra instructions for Cara that apply to the whole workbook, for example which
                  totals must stay untouched or how numbers should be formatted. Per-tab guidance
                  can be added after the workbook is processed.
                </Description>
                <AutoResizeTextarea
                  invalid={!!getValidationError(validationErrors, ['fillGuidance'])}
                  value={form.fillGuidance ?? ''}
                  onChange={(e) => {
                    const fillGuidance = e.currentTarget.value;
                    updateForm(['fillGuidance'], (prev) => ({ ...prev, fillGuidance }));
                  }}
                  rows={4}
                  placeholder="Example: Never overwrite the totals row; it is calculated by formulas."
                />
                {getValidationError(validationErrors, ['fillGuidance']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['fillGuidance'])}
                  </ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Excel workbook (.xlsx)</Label>
                <Description>
                  .xlsx only. Not .xls, .xlsm, or .csv. One file per upload.
                </Description>
                <FileInput
                  key={fileInputKey}
                  invalid={!!fileError}
                  onChange={(change) => setUploadTemplate(change.files[0]?.upload ?? null)}
                />
                {fileError && <ErrorMessage>{fileError}</ErrorMessage>}
              </Field>

              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" onClick={handleClose} disabled={fetcherInFlight}>
            Cancel
          </Button>
          {visibilityEnabled ? (
            <VisibilitySplitButton
              type="submit"
              value={visibility}
              onChange={setVisibility}
              loading={fetcherInFlight}
            >
              Upload template
            </VisibilitySplitButton>
          ) : (
            <Button type="submit" color="sky" loading={fetcherInFlight}>
              Upload template
            </Button>
          )}
        </DialogActions>
      </Form>
    </Dialog>
  );
}
